// =============================================================================
// Origin UI — src/services/apiService.ts
// Centralized HTTP client for making authenticated calls to the Origin API.
// Automatically acquires bearer tokens via MSAL and attaches them.
// All API calls go through this service to ensure consistent auth and error handling.
// =============================================================================

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from '@/config/authConfig';

/** Base URL for the API — in dev, Vite proxies /api to the backend */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/** Singleton MSAL instance — set during app initialization */
let msalInstance: PublicClientApplication | null = null;

/**
 * Initialize the API service with the MSAL instance.
 * Must be called once at app startup.
 */
export function initApiService(instance: PublicClientApplication): void {
  msalInstance = instance;
}

/**
 * Acquire an access token for the Origin API.
 * Uses the ACTIVE account (set by LOGIN_SUCCESS handler) so we never
 * accidentally grab a cached @oq.com token when the user logged in
 * with @omantrading.com.
 */
async function getAccessToken(): Promise<string> {
  if (!msalInstance) throw new Error('API service not initialized');

  // Prefer the active account (set on login). Only fall back to
  // getAllAccounts()[0] if no active account is set yet.
  const account = msalInstance.getActiveAccount()
    ?? msalInstance.getAllAccounts()[0]
    ?? null;

  if (!account) throw new Error('No authenticated account');

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: apiScopes.default,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const response = await msalInstance.acquireTokenPopup({
        scopes: apiScopes.default,
        account,
      });
      return response.accessToken;
    }
    throw error;
  }
}

/**
 * Make an authenticated API request.
 * Automatically attaches the bearer token and handles common error patterns.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new ApiError(response.status, response.statusText, errorBody);
  }

  // Handle 204 No Content
  if (response.status === 204) return undefined as T;

  return response.json();
}

/**
 * Custom error class for API errors with status code and body.
 * For permission/auth failures we surface a human-readable message instead of
 * the raw "API Error 403: Forbidden", since that text is shown to end users.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: string
  ) {
    super(ApiError.friendlyMessage(status, statusText, body));
    this.name = 'ApiError';
  }

  /** Pull a human-readable message out of the API response body, if there is one. */
  private static extractServerMessage(body: string): string | null {
    if (!body) return null;
    const trimmed = body.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      const msg = parsed.message || parsed.error || parsed.detail || parsed.title;
      if (typeof msg === 'string' && msg.trim()) return msg.trim();
      // ASP.NET model validation shape: { errors: { field: ["message"] } }
      if (parsed.errors && typeof parsed.errors === 'object') {
        for (const value of Object.values(parsed.errors)) {
          if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
          if (typeof value === 'string') return value;
        }
      }
      return null;
    } catch {
      // Not JSON — only trust short, non-HTML plain text.
      if (trimmed.length <= 300 && !trimmed.startsWith('<')) return trimmed;
      return null;
    }
  }

  private static friendlyMessage(status: number, statusText: string, body: string): string {
    // Keep auth failures as fixed, reassuring text.
    if (status === 403)
      return "You don't have permission to view this. If you think this is a mistake, contact HR or IT.";
    if (status === 401)
      return 'Your session has expired. Please refresh the page and sign in again.';

    // For validation / business-rule errors (400, 409, 422, ...) the backend's own
    // message is the most useful thing to show the user — e.g. an overlap or
    // insufficient-balance explanation — rather than a raw "API Error 400".
    const serverMessage = ApiError.extractServerMessage(body);
    if (serverMessage) return serverMessage;

    if (status === 404)
      return 'That item could not be found. It may have been removed.';
    if (status >= 500)
      return 'Something went wrong on our side. Please try again — if it keeps happening, contact IT.';
    return `API Error ${status}: ${statusText}`;
  }
}

// Convenience methods
export const api = {
  get: <T>(path: string) => apiRequest<T>(path, { method: 'GET' }),

  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),

  patch: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /** Upload a file via multipart/form-data. Do NOT set Content-Type — browser sets boundary. */
  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const token = await getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const errorBody = await response.text();
      throw new ApiError(response.status, response.statusText, errorBody);
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  },

  /** Get an access token (for direct fetch calls like file downloads). */
  getToken: async (): Promise<string> => {
    return await getAccessToken();
  },
};
